/**
 * An event's featured species, widened to their WHOLE evolution line (the
 * 2026-10-20 owner ask: the Dex's Evo toggle on the event gap strips, "so
 * that someone can easily update the entire line"; 2026-10-31 device
 * report: a Tyranitar or Mega Chandelure Raid Day tile brought nothing,
 * while the Dex's toggle showed Litwick, Lampent and Chandelure).
 *
 * Both directions, stage by stage, like the Dex's "Whole line": a Sneasel
 * Max Monday brings Weavile, an Electabuzz spawn brings Elekid and
 * Electivire, a Tyranitar Raid Day brings Larvitar and Pupitar. Alolan
 * Geodude brings Alolan Graveler and Alolan Golem.
 *
 * Why widen the SPAWNS rather than the gaps: everything downstream of a spawn
 * list already knows what to do with it. raidDexGaps derives the variants
 * from the trainer's foci or the admin's ticks, eventDexGaps gates each row
 * on availability, the strips freeze and commit it. A derived spawn is just
 * one more entry in that list, carrying its source's variants, so the added
 * stages get exactly the rows the featured stage gets and nothing a human
 * would have had to tick again.
 *
 * Three kinds of form ride along differently:
 *   - A costume or regional form follows the record page's "Evolved it?" rule
 *     (lib/costumeEvolution), in both directions: costume Sinistea brings the
 *     costume Polteageist, Alolan Sandslash brings Alolan Sandshrew, costume
 *     Pikachu brings nothing (there is no costume Raichu). An evolution only
 *     a regional form has (Hisuian Sneasel to Sneasler, Galarian Meowth to
 *     Perrserker) is taken only by that form and lands on the plain species;
 *     walked back, Sneasler brings Hisuian Sneasel.
 *   - The reserved "dynamax" / "mega" form ids a reviewed Max or Mega event
 *     files its Shiny Dynamax / 100% Mega copies under (lib/eventLinkDefaults)
 *     are not costumes. They ride along unchanged to every stage that can
 *     carry them, so Dynamax Sneasel brings Dynamax Weavile with its shiny,
 *     lucky and 100% copies. On a stage that cannot (Litwick under a Mega
 *     Chandelure), the copies land as the stage's plain rows: a Lucky Mega
 *     Chandelure tile brings Lucky Litwick and Lucky Lampent.
 *   - A feed spawn's regional suffix ("alola") is resolved to the form and
 *     follows the first rule; the derived stage keeps the suffix only while
 *     the stage has that form.
 *
 * Every derived variant is gated on the stage's own availability (the shiny
 * roster, the Dynamax and Mega rosters, a costume's own shiny release), so no
 * strip offers a tile nobody can fill. Pure, no DOM.
 */
import { evolvesFrom, evolvesInto } from "./evolutionFamily";
import { evolvedFormIdFor, formSuffix } from "./costumeEvolution";
import { isEventFormAvailable } from "./eventFormAvailability";
import { isVariantAvailable } from "./variantAvailability";
import { DYNAMAX_FORM_ID } from "./dynamaxDex";
import { isMegaFormId } from "./megaDex";
import { speciesName } from "@/data/speciesNames";
import { FORM_GROUPS_BY_SPECIES_ID } from "./generated/pokemonForms.generated";
import type { RaidDexVariant, RaidSpawnInput } from "./raidDexCrossRef";

type SpawnForm = { formId: string; variants?: RaidDexVariant[] };

/** One stage above a species: the species, and the form it keeps (null = plain). */
export interface EvolutionStage {
  speciesId: number;
  formId: string | null;
}

function validId(id: unknown): id is number {
  return typeof id === "number" && Number.isFinite(id) && id >= 1;
}

/** The registry form of `speciesId` whose id ends with `-suffix`, or null. */
function formWithSuffix(speciesId: number, suffix: string): string | null {
  const group = FORM_GROUPS_BY_SPECIES_ID.get(speciesId);
  return group?.forms.find((f) => f.formId.endsWith(`-${suffix}`))?.formId ?? null;
}

/** A reviewed event's reserved Dynamax / Mega form id, never a costume. */
function isKindTag(formId: string): boolean {
  return formId === DYNAMAX_FORM_ID || isMegaFormId(formId);
}

/**
 * The rest of the line of (speciesId, formId), both directions, in dex
 * order, each stage with the form it keeps. A costume with no counterpart
 * on a stage ends that branch; an evolution only a regional form has is
 * taken only by that form and lands on the plain species, and walked back
 * lands on that regional form.
 */
export function evolutionStagesFor(speciesId: number, formId: string | null | undefined): EvolutionStage[] {
  const out: EvolutionStage[] = [];
  const seen = new Set<number>([speciesId]);
  const queue: EvolutionStage[] = [{ speciesId, formId: formId ?? null }];
  const visit = (stage: EvolutionStage) => {
    seen.add(stage.speciesId);
    out.push(stage);
    queue.push(stage);
  };
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const curSuffix = cur.formId ? formSuffix(cur.formId) : null;
    for (const edge of evolvesInto(cur.speciesId)) {
      if (seen.has(edge.to)) continue;
      let next: string | null;
      if (edge.region) {
        // Only that regional form takes this edge, onto a species with no
        // regional form of its own (Sneasler, Perrserker, Obstagoon).
        if (curSuffix !== edge.region) continue;
        next = null;
      } else if (cur.formId == null) {
        next = null;
      } else {
        next = evolvedFormIdFor(cur.formId, edge.to);
        if (next == null) continue; // the costume / region stops here
      }
      visit({ speciesId: edge.to, formId: next });
    }
    for (const edge of evolvesFrom(cur.speciesId)) {
      if (seen.has(edge.from)) continue;
      let prev: string | null;
      if (edge.region) {
        // Back over a regional-only edge: onto that regional form, which a
        // costumed stage (none exist today) could not have come from.
        if (cur.formId != null) continue;
        prev = formWithSuffix(edge.from, edge.region);
        if (prev == null) continue;
      } else if (cur.formId == null) {
        prev = null;
      } else {
        prev = evolvedFormIdFor(cur.formId, edge.from);
        if (prev == null) continue;
      }
      visit({ speciesId: edge.from, formId: prev });
    }
  }
  return out.sort((a, b) => a.speciesId - b.speciesId);
}

/**
 * The spawns plus, after each one, the rest of its line, each added
 * stage carrying the source's variants and shiny boost where the stage can
 * have them. A stage the event already lists is not added twice; a stage two
 * spawns both reach is added once with both sets of rows.
 */
export function expandSpawnsToLines(spawns: readonly RaidSpawnInput[]): RaidSpawnInput[] {
  const listed = new Set<number>();
  for (const s of spawns) if (validId(s.speciesId)) listed.add(s.speciesId);
  const out: RaidSpawnInput[] = [];
  const added = new Map<number, RaidSpawnInput>();
  for (const s of spawns) {
    out.push(s);
    if (!validId(s.speciesId)) continue;
    for (const d of derivedSpawns(s)) {
      const id = d.speciesId as number;
      if (listed.has(id)) continue;
      const prior = added.get(id);
      if (prior) {
        mergeSpawn(prior, d);
        continue;
      }
      added.set(id, d);
      out.push(d);
    }
  }
  return out;
}

/** The rest of one spawn's line, as spawns, in dex order. */
function derivedSpawns(s: RaidSpawnInput): RaidSpawnInput[] {
  const id = s.speciesId as number;
  const forms = (s.forms ?? []).filter((f) => f.formId);
  const realForms = forms.filter((f) => !isKindTag(f.formId));
  const tagForms = forms.filter((f) => isKindTag(f.formId));
  const variants = s.variants ?? [];
  // No curated rows at all: the foci path, which the derived stages share.
  const fociPath = variants.length === 0 && forms.length === 0;
  const byId = new Map<number, RaidSpawnInput>();
  const order: number[] = [];
  const stageSpawn = (to: number): RaidSpawnInput => {
    let d = byId.get(to);
    if (!d) {
      d = { speciesId: to, name: speciesName(to) };
      if (s.shinyBoost) d.shinyBoost = true;
      byId.set(to, d);
      order.push(to);
    }
    return d;
  };

  // Species-level rows (and the Dynamax / Mega tags, which are species-level
  // in all but storage) walk the plain line, or the feed's regional one.
  if (variants.length > 0 || tagForms.length > 0 || fociPath) {
    const sourceForm = s.formSuffix ? formWithSuffix(id, s.formSuffix) : null;
    for (const st of evolutionStagesFor(id, sourceForm)) {
      // A species-level Mega / Dynamax chase on a stage that cannot (Litwick
      // under Mega Chandelure) is the stage's plain entry.
      let v: RaidDexVariant[] = [];
      for (const x of variants) {
        if (isVariantAvailable(x, st.speciesId)) v = unionVariants(v, [x]);
        else if ((x === "mega" || x === "dynamax") && isVariantAvailable("pokemon", st.speciesId)) v = unionVariants(v, ["pokemon"]);
      }
      const tags: SpawnForm[] = [];
      for (const f of tagForms) {
        const tag = tagForStage(f, st.speciesId);
        if (tag) {
          tags.push(tag);
          continue;
        }
        // The stage cannot Mega / Dynamax: the copies land as its plain rows.
        const plain: RaidDexVariant[] = f.variants && f.variants.length > 0 ? f.variants : ["pokemon"];
        v = unionVariants(v, plain.filter((x) => isVariantAvailable(x, st.speciesId)));
      }
      if (!fociPath && v.length === 0 && tags.length === 0) continue;
      const d = stageSpawn(st.speciesId);
      if (v.length > 0) d.variants = unionVariants(d.variants, v);
      if (tags.length > 0) d.forms = unionForms(d.forms, tags);
      if (st.formId) {
        // A regional stage (Hisuian Sneasel under Sneasler, Alolan Graveler
        // under Alolan Geodude) keeps its form as the feed's suffix.
        const suffix = formSuffix(st.formId);
        if (suffix) d.formSuffix = suffix;
      }
    }
  }

  // Each costume / regional form walks its own line, stage by stage.
  for (const f of realForms) {
    for (const st of evolutionStagesFor(id, f.formId)) {
      if (st.formId) {
        const form = formForStage(f, st.formId, st.speciesId);
        if (!form) continue;
        const d = stageSpawn(st.speciesId);
        d.forms = unionForms(d.forms, [form]);
      } else {
        // A regional form's evolution with no regional form of its own: the
        // form's rows become species-level rows on the plain stage.
        const wanted: RaidDexVariant[] = f.variants && f.variants.length > 0 ? f.variants : ["pokemon", "shiny"];
        const v = wanted.filter((x) => isVariantAvailable(x, st.speciesId));
        if (v.length === 0) continue;
        const d = stageSpawn(st.speciesId);
        d.variants = unionVariants(d.variants, v);
      }
    }
  }

  return order.map((to) => byId.get(to)!);
}

/** A Dynamax / Mega tag on a stage that can carry it, its copies gated, or null. */
function tagForStage(f: SpawnForm, speciesId: number): SpawnForm | null {
  const kind: RaidDexVariant = f.formId === DYNAMAX_FORM_ID ? "dynamax" : "mega";
  if (!isVariantAvailable(kind, speciesId)) return null;
  if (!f.variants || f.variants.length === 0) {
    return isEventFormAvailable("pokemon", f.formId, speciesId) ? { formId: f.formId } : null;
  }
  const v = f.variants.filter((x) => isEventFormAvailable(x, f.formId, speciesId));
  return v.length > 0 ? { formId: f.formId, variants: v } : null;
}

/** The source form's rows on the evolved form, gated on that form, or null. */
function formForStage(f: SpawnForm, formId: string, speciesId: number): SpawnForm | null {
  if (!f.variants || f.variants.length === 0) {
    // raidDexGaps reads a form with no rows as Main + Shiny; keep the form
    // while its Main exists, and let the strips gate the shiny.
    return isEventFormAvailable("pokemon", formId, speciesId) ? { formId } : null;
  }
  const v = f.variants.filter((x) => isEventFormAvailable(x, formId, speciesId));
  return v.length > 0 ? { formId, variants: v } : null;
}

function unionVariants(a: RaidDexVariant[] | undefined, b: readonly RaidDexVariant[]): RaidDexVariant[] {
  const out = [...(a ?? [])];
  for (const v of b) if (!out.includes(v)) out.push(v);
  return out;
}

function unionForms(a: SpawnForm[] | undefined, b: readonly SpawnForm[]): SpawnForm[] {
  const out = (a ?? []).map((f) => ({ ...f, ...(f.variants ? { variants: [...f.variants] } : {}) }));
  for (const f of b) {
    const prior = out.find((x) => x.formId === f.formId);
    if (!prior) {
      out.push({ ...f, ...(f.variants ? { variants: [...f.variants] } : {}) });
    } else if (f.variants && f.variants.length > 0) {
      prior.variants = unionVariants(prior.variants, f.variants);
    }
  }
  return out;
}

/** Fold one derived spawn into an earlier one for the same species. */
function mergeSpawn(into: RaidSpawnInput, from: RaidSpawnInput): void {
  if (from.shinyBoost) into.shinyBoost = true;
  if (from.variants && from.variants.length > 0) into.variants = unionVariants(into.variants, from.variants);
  if (from.forms && from.forms.length > 0) into.forms = unionForms(into.forms, from.forms);
  if (!into.formSuffix && from.formSuffix) into.formSuffix = from.formSuffix;
}

/** A shiny-strip target widened by its line: `via` names the featured stage it came from. */
export interface ShinyLineTarget {
  speciesId: number;
  formId?: string;
  via?: number;
}

/**
 * Shiny-strip targets (the Shiny Hunt block's tiles) plus, after each one,
 * the rest of its line where the shiny can exist, each marked with the
 * stage it came from. Nothing already on the strip is added twice.
 */
export function expandShinyTargetsToLines(
  targets: readonly { speciesId: number; formId?: string }[],
): ShinyLineTarget[] {
  const key = (t: { speciesId: number; formId?: string }) => `${t.speciesId}:${t.formId ?? ""}`;
  const seen = new Set(targets.map(key));
  const out: ShinyLineTarget[] = [];
  for (const t of targets) {
    out.push({ speciesId: t.speciesId, ...(t.formId ? { formId: t.formId } : {}) });
    for (const st of evolutionStagesFor(t.speciesId, t.formId ?? null)) {
      const d: ShinyLineTarget = { speciesId: st.speciesId, ...(st.formId ? { formId: st.formId } : {}), via: t.speciesId };
      const k = key(d);
      if (seen.has(k)) continue;
      const canBeShiny = st.formId
        ? isEventFormAvailable("shiny", st.formId, st.speciesId)
        : isVariantAvailable("shiny", st.speciesId);
      if (!canBeShiny) continue;
      seen.add(k);
      out.push(d);
    }
  }
  return out;
}
